<ng-template #actions>
  <div class="flex items-center gap-3">
    <button
      type="button"
      class="flex items-center justify-center w-10 h-10 rounded-full bg-[#eeeeee] cursor-pointer transition-colors hover:bg-black/5"
      [attr.aria-label]="'HEADER.NOTIFICATIONS' | translate"
      (click)="openAlertCenter()"
    >
      <ng-icon name="heroBell" class="text-[20px]" aria-hidden="true" />
    </button>

    <!-- Foto do perfil baixada por `user/avatar`; sem foto, a inicial do nome. -->
    @if (avatarUrl(); as photo) {
      <img
        class="w-10 h-10 rounded-full object-cover"
        [src]="photo"
        alt=""
        aria-hidden="true"
        width="40"
        height="40"
      />
    } @else {
      <span
        class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#e0e0e0] text-[18px] font-medium text-[#212121] select-none"
        >{{ userInitial() }}</span
      >
    }

    <strong class="hidden lg:block text-body font-bold">{{ user()?.name }}</strong>

    <!-- Abre no hover; o clique cobre toque e teclado, onde não existe hover. -->
    <div
      #userMenu
      class="relative"
      tabindex="0"
      (pointerenter)="onUserMenuPointerEnter($event)"
      (pointerleave)="onUserMenuPointerLeave($event)"
      (focusout)="onUserMenuFocusOut($event)"
      (keydown.escape)="closeUserMenu()"
    >
      <button
        type="button"
        class="flex items-center justify-center w-10 h-10 rounded-full cursor-pointer text-[#9e9e9e] transition-colors hover:bg-black/5"
        [attr.aria-label]="'HEADER.MORE_OPTIONS' | translate"
        [attr.aria-expanded]="userMenuOpen()"
        aria-haspopup="true"
        (click)="toggleUserMenu()"
      >
        <ng-icon name="heroEllipsisVertical" class="text-[24px]" aria-hidden="true" />
      </button>
      @if (userMenuOpen()) {
        <!-- O `pt-2` é o espaço visual até o botão; como padding, mantém o hover contínuo. -->
        <div class="absolute right-0 top-full pt-2 z-50">
          <div
            class="w-max min-w-50 py-2 rounded-lg bg-white shadow-lg [&_button]:whitespace-nowrap"
          >
            <button
              type="button"
              (click)="openMyAccount()"
              class="flex items-center gap-3 w-full px-4 py-2.5 text-left text-body text-[#212121] cursor-pointer transition-colors hover:bg-black/5"
            >
              <ng-icon
                name="heroUserCircle"
                class="text-[20px] text-[#5f6368]"
                aria-hidden="true"
              />
              {{ 'MY_ACCOUNT.TITLE' | translate }}
            </button>
            @if (showOrganizationSettings()) {
              <button
                type="button"
                (click)="openOrganizationSettings()"
                class="flex items-center gap-3 w-full px-4 py-2.5 text-left text-body text-[#212121] cursor-pointer transition-colors hover:bg-black/5"
              >
                <ng-icon
                  name="heroCog6Tooth"
                  class="text-[20px] text-[#5f6368]"
                  aria-hidden="true"
                />
                {{ 'NAV.ORGANIZATION_SETTINGS' | translate }}
              </button>
            }
            <button
              type="button"
              class="flex items-center gap-3 w-full px-4 py-2.5 text-left text-body text-[#212121] cursor-pointer transition-colors hover:bg-black/5"
            >
              <ng-icon
                name="heroChatBubbleOvalLeft"
                class="text-[20px] text-[#5f6368]"
                aria-hidden="true"
              />
              {{ 'HEADER.SUPPORT' | translate }}
            </button>
            <button
              type="button"
              (click)="logout()"
              class="flex items-center gap-3 w-full px-4 py-2.5 text-left text-body text-[#212121] cursor-pointer transition-colors hover:bg-black/5"
            >
              <ng-icon
                name="heroArrowRightStartOnRectangle"
                class="text-[20px] text-[#5f6368]"
                aria-hidden="true"
              />
              {{ 'HEADER.LOGOUT' | translate }}
            </button>
          </div>
        </div>
      }
    </div>
  </div>
</ng-template>

@if (actionsOnly()) {
  <ng-container *ngTemplateOutlet="actions" />
} @else {
  <div
    class="flex w-full min-w-0 max-w-full flex-wrap items-start justify-between gap-x-8 gap-y-4 pt-7.5 max-mobile:gap-2 max-mobile:pt-2"
  >
    <!--
    The date and the account stay one group on the first row, aligned with the
    title. The title column shrinks and wraps; the cluster does not drop to a
    second line. The breadcrumb is `basis-full`, so it is the only piece that
    moves down. Below 48rem the cluster is hidden and the date sits under the title.
  -->
    <div class="order-1 min-w-0 max-w-full flex-1">
      <div class="flex items-center gap-1.5">
        <h1
          class="text-page mb-5 text-black [@media(max-height:820px)]:mb-2 max-mobile:mb-0 max-mobile:text-section"
        >
          {{ title() | translate }}
        </h1>
        <!-- No mobile a frase sai do fluxo e abre por aqui; o leitor de tela a alcança pelo botão. -->
        <button
          type="button"
          data-header-phrase-toggle
          class="hidden h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-[#6B7281] transition-colors hover:bg-black/5 max-mobile:inline-flex"
          [attr.aria-label]="'HEADER.ABOUT_SCREEN' | translate"
          [attr.aria-expanded]="phraseOpen()"
          aria-controls="dashboard-header-phrase"
          (click)="togglePhrase()"
        >
          <ng-icon name="heroInformationCircle" class="text-[18px]" aria-hidden="true" />
        </button>
      </div>
      @if (lastUpdate(); as date) {
        <p
          data-header-last-update-compact
          class="m-0 hidden gap-1 text-label text-[#6B7281] max-mobile:flex"
        >
          <strong class="font-medium">{{ 'HEADER.LAST_UPDATE' | translate }}</strong>
          <span>{{ date | date: 'dd/MM/yyyy HH:mm' }}</span>
        </p>
      }
      <p
        id="dashboard-header-phrase"
        class="mt-0 min-w-0 text-body text-balance break-words text-[#6B7281] max-mobile:mt-1"
        [class.max-mobile:hidden]="!phraseOpen()"
      >
        {{ phrase() | translate: phraseParams() }}
      </p>
    </div>

    <!-- Abaixo de 48rem as ações sobem para a barra do layout, ao lado do menu, e a data
       desce para baixo do título (decisão de produto 2026-10-26). -->
    <div class="order-2 flex shrink-0 items-center justify-end gap-6 self-start max-mobile:hidden">
      <!-- Só no detalhe de VCR com `updated_at` real; o shell do detalhe é quem preenche. -->
      @if (lastUpdate(); as date) {
        <div class="flex items-center gap-2 text-body">
          <strong>{{ 'HEADER.LAST_UPDATE' | translate }}</strong>
          <span>{{ date | date: 'dd/MM/yyyy HH:mm' }}</span>
        </div>
      }

      <ng-container *ngTemplateOutlet="actions" />
    </div>
    <app-breadcrumb class="order-3 min-w-0 w-full max-w-full basis-full" [items]="breadcrumb()" />
  </div>
}
